import Head from "next/head";
import { Fragment, useEffect, useMemo } from "react";
import { WithStore, WithTheme } from "../hooks";
import { WithNotifications } from "../hooks/useNotifications";
// import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/antd.less";
import "../styles/global.css";
import "../components/SharedComponents/i18n";
import { WithWorker } from "../hooks/useWorker";
import { isMobile, isTablet } from "react-device-detect";
import { MobileUnsupported } from "../components/MobileUnsupported";
import ProgressBar from "@badrap/bar-of-progress";
import router, { useRouter } from "next/router";
import { COLORS } from "../const";
import { createGlobalStyle } from "styled-components";
import { ErrorBoundary } from "../components/SharedComponents";
import IpCheckWrapper from "../components/IpCheckWrapper";
import SessionChecker from "../components/SessionChecker";
import BuildVersionMessage from "../components/BuildVersionMessage";
import { SWRConfig } from "swr";
import { globalSuccessNotification } from "../components/SharedComponents/notification";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import fetcher, { DEFAULT_ERROR_RETRY_COUNT } from "../utils/common/fetcher";

const ENABLE_MOBILE_REDIRECT = false;

const GlobalStyle = createGlobalStyle`
  :root {
    ${Object.keys(COLORS)
      .map((key) => `--${key.toLowerCase().replace(/[%]+/gim, "")}: ${COLORS[key]};`)
      .join("\n")}
  }
`;

const progress = new ProgressBar({
  size: 2,
  color: COLORS["CK2"],
  className: "bar-of-progress",
  delay: 100,
});

router.events.on("routeChangeStart", progress.start);
router.events.on("routeChangeComplete", progress.finish);
router.events.on("routeChangeError", progress.finish);

function gtag() {
  window.dataLayer.push(arguments);
}

// To expose HTML twitter card headers, pages below will need to bypass ip whitelist check here in _app.js
// These pages will have ip whitelist check inside.
const TWITTER_CARD_URL_WHITELIST = [
  // "/signup",
  "/shared-report",
  "/shared-report/incremental",
  "/report",
  /* deprecated report urls below, kept for backward compatibility: */
  "/findings", // use /report/[reportId] instead
  "/findings/incremental", // use /report/[reportId] instead
  "/findings/marketplace", // use /report/[reportId]?reportType=marketplace instead
];

const SESSION_CHECK_BYPASS_WHITELIST = [
  "/login",
  "/signup",
  "/reset-password",
  "/shared-report",
  "/shared-report/incremental",
  "/404",
  "/landing", // All landing pages: slack, slack-support, badges
  "/privacy-policy",
];

const swrConfig = {
  fetcher,
  onErrorRetry: (error, key, config, revalidate, revalidateOpts) => {
    /* Only retry 504 */
    if (error.status !== 504) {
      return;
    }

    /* Base implementation adopted from SWR follows */

    const maxRetryCount = config.errorRetryCount;
    const currentRetryCount = revalidateOpts.retryCount || DEFAULT_ERROR_RETRY_COUNT;

    // Exponential backoff
    const timeout =
      Math.trunc((Math.random() + 0.5) * 2 ** Math.min(currentRetryCount, 8)) *
      config.errorRetryInterval;

    if (maxRetryCount !== null && currentRetryCount > maxRetryCount) {
      return;
    }

    setTimeout(() => {
      revalidate(revalidateOpts);
    }, timeout);
  },
};

export default function MyApp({ Component, pageProps, initialProps }) {
  const { asPath } = initialProps;
  const bypassServerSide = TWITTER_CARD_URL_WHITELIST.some((url) => asPath.includes(url));

  const router = useRouter();

  const bypassSessionCheck = useMemo(() => {
    const path = router.asPath || "";
    return SESSION_CHECK_BYPASS_WHITELIST.some((url) => {
      return path.includes(url);
    });
  }, [router]);

  useEffect(() => {
    if (
      process.env.NEXT_PUBLIC_ENV === "production" &&
      typeof window !== "undefined" &&
      window.location.origin !== "http://localhost:8889"
    ) {
      let console = window.console || {};
      console.log = function () {};
      console.error = function () {};
    }

    if (process.env.NEXT_PUBLIC_ENV !== "development") {
      // Google Analytics
      window.dataLayer = window.dataLayer || [];
      gtag("js", new Date());
      gtag("config", "G-PNDNLEL9R3");
    }

    window.addEventListener("storage", (event) => {
      if (
        event.storageArea === localStorage &&
        event.key === "comment_report_downloaded" &&
        event.newValue === "true"
      ) {
        console.log(event);
        globalSuccessNotification("Comment Report Downloaded Successfully");
        window.localStorage.removeItem("comment_report_downloaded");
      }
    });

    // For PWA support
    if ("serviceWorker" in navigator) {
      // Reads serviceWorker.js in public folder
      navigator.serviceWorker.register("/serviceWorker.js").then(
        function (registration) {
          console.log("Service Worker registration successful with scope: ", registration.scope);
        },
        function (err) {
          console.log("Service Worker registration failed: ", err);
        }
      );
    }
  }, []);

  // Optional mobile redirect; disabled by default.
  if (
    ENABLE_MOBILE_REDIRECT &&
    process.env.NEXT_PUBLIC_APP === "skyharbor" &&
    (isMobile || isTablet)
  ) {
    if (process.env.NEXT_PUBLIC_MOBILE_HOST) {
      // TODO: better handling
      const href = window.location.href.replace(
        window.location.host,
        process.env.NEXT_PUBLIC_MOBILE_HOST
      );
      window.location.replace(href);
      // stop js process
      throw SyntaxError();
    } else {
      return <MobileUnsupported />;
    }
  }

  return (
    <Fragment>
      <Head>
        <link rel="manifest" href="manifest.json" />
        <link rel="icon" href="/images/favicon.ico" />
        {process.env.NEXT_PUBLIC_ENV === "staging" && (
          <meta
            name="google-site-verification"
            content="198FfZA2rG1RDePo6NU10OiooJKyxTWW8ExBuYSQr2M"
          />
        )}
        {process.env.NEXT_PUBLIC_ENV === "staging" && <meta name="robots" content="noIndex" />}
      </Head>
      <IpCheckWrapper bypass={bypassServerSide}>
        <GlobalStyle />
        <WithStore>
          {!bypassSessionCheck && <SessionChecker />}
          <WithTheme>
            <WithWorker>
              <ErrorBoundary>
                <SWRConfig
                  value={{
                    ...swrConfig,
                    fallback: pageProps?.fallback ?? {},
                  }}
                >
                  <WithNotifications>
                    <Component {...pageProps} />
                  </WithNotifications>
                </SWRConfig>
              </ErrorBoundary>
            </WithWorker>
            <BuildVersionMessage />
          </WithTheme>
        </WithStore>
      </IpCheckWrapper>
      <Analytics />
      <SpeedInsights route={router.pathname} />
    </Fragment>
  );
}

MyApp.getInitialProps = function (context) {
  const { router } = context;
  const { asPath } = router;

  return { initialProps: { asPath } };
};
